import { useEffect, useMemo, useState } from "react";
import { FileText, Loader2, AlertCircle, Calendar as CalendarIcon } from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { auth } from "../config/firebase";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API_BASE_URL = import.meta.env.VITE_API_URL;
const MASJID_API_URL = import.meta.env.VITE_MASJID_API;

function getYearMonthOptions() {
  const now = new Date();
  const options = [];

  // Current month
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Next month
  const nextDate = new Date(currentYear, currentMonth + 1, 1);

  [new Date(currentYear, currentMonth, 1), nextDate].forEach((d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const key = `${year}-${month}`;
    const label = d.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
    options.push({ value: key, label });
  });

  return options;
}

export default function MonthlyTimes() {
  const { user } = useAuth();
  const [masjid, setMasjid] = useState(null);
  const [loadingMasjid, setLoadingMasjid] = useState(true);
  const [masjidError, setMasjidError] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [activeMonth, setActiveMonth] = useState(null);
  const [generateError, setGenerateError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generatedMonthLabel, setGeneratedMonthLabel] = useState(null);

  const monthOptions = useMemo(() => getYearMonthOptions(), []);

  useEffect(() => {
    const loadMasjid = async () => {
      if (!user) return;

      try {
        setLoadingMasjid(true);
        setMasjidError(null);

        const token = await auth.currentUser?.getIdToken();
        if (!token) {
          throw new Error("Authentication required");
        }

        const response = await fetch(`${API_BASE_URL}/v1/masjid/admin/info`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          let message = "Failed to load masjid info";
          try {
            const data = await response.json();
            if (data?.message) message = data.message;
          } catch {
            // ignore parse errors
          }
          throw new Error(message);
        }

        const data = await response.json();
        setMasjid({
          ...(data.masjid || {}),
          subscription: data.masjid?.subscription || "basic",
        });
      } catch (err) {
        console.error("MonthlyTimes: loadMasjid error", err);
        setMasjidError(err.message || "Failed to load masjid");
      } finally {
        setLoadingMasjid(false);
      }
    };

    loadMasjid();
  }, [user]);

  const runGenerate = async (monthKey) => {
    if (!masjid?.id || !monthKey) return;
    setGenerateError(null);
    setPdfUrl(null);

    if ((masjid.subscription || "basic") !== "pro") {
      setGenerateError(
        "Monthly PDF generation is available for Pro plan masajid only. Use the Subscription option in your sidebar to upgrade."
      );
      return;
    }

    try {
      setActiveMonth(monthKey);
      setGenerating(true);
      const token = await auth.currentUser?.getIdToken(true);
      if (!token) {
        throw new Error("Authentication required");
      }

      const resp = await fetch(
        `${MASJID_API_URL}/v1/masjid/${encodeURIComponent(
          masjid.id
        )}/generate-pdf/${encodeURIComponent(monthKey)}?regenerate=true`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        throw new Error(
          data?.error || "Failed to generate monthly prayer times PDF"
        );
      }

      if (!data?.url) {
        throw new Error("PDF URL missing from response");
      }

      setPdfUrl(data.url);
      const matchedMonth = monthOptions.find((opt) => opt.value === monthKey);
      setGeneratedMonthLabel(matchedMonth?.label || null);
    } catch (err) {
      console.error("MonthlyTimes: generate error", err);
      setGenerateError(err.message || "Failed to generate PDF");
    } finally {
      setGenerating(false);
      setActiveMonth(null);
    }
  };

  const isProMasjid = masjid && (masjid.subscription || "basic") === "pro";

  return (
    <AppLayout>
      <div className="relative min-h-screen">
        {/* Subtle background */}
        <div className="pointer-events-none fixed inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-background to-background" />
          <div className="absolute left-1/3 top-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
        </div>

        <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
          <Card className="border-none bg-card/80 shadow-xl backdrop-blur-sm">
            <CardHeader className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <FileText className="h-5 w-5" />
                </div>
                <div>
                  <CardTitle className="text-xl sm:text-2xl">
                    Monthly Prayer Times PDF
                  </CardTitle>
                  <CardDescription>
                    Generate a clean, single-page PDF of this month&apos;s
                    prayer times for printing or sharing.
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {loadingMasjid ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>Loading masjid details...</span>
                </div>
              ) : masjidError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{masjidError}</AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="rounded-lg bg-muted/50 px-4 py-3 text-sm">
                    <div className="min-w-0">
                      <p className="font-medium">
                        {masjid?.name || "Your masjid"}
                      </p>
                      <p className="truncate text-xs text-muted-foreground">
                        {masjid?.city && masjid?.state
                          ? `${masjid.city}, ${masjid.state}`
                          : masjid?.address || "Location not set"}
                      </p>
                    </div>
                  </div>

                  {!isProMasjid ? (
                    <Alert>
                      <AlertDescription>
                        Monthly PDF generation is available for Pro plan masjids only. Use the Subscription option in your sidebar to upgrade.
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <>
                      {monthOptions.length > 0 && (
                        <div className="grid gap-3 sm:grid-cols-2">
                          {monthOptions.map((opt) => (
                      <Button
                              key={opt.value}
                        className="w-full gap-2"
                              onClick={() => runGenerate(opt.value)}
                        disabled={generating || !masjid?.id}
                      >
                              {generating && activeMonth === opt.value ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                                  <span>Generating {opt.label} PDF...</span>
                          </>
                        ) : (
                          <>
                            <CalendarIcon className="h-4 w-4" />
                                  <span>Generate {opt.label} PDF</span>
                          </>
                        )}
                      </Button>
                          ))}
                    </div>
                      )}

                  {generateError && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{generateError}</AlertDescription>
                    </Alert>
                  )}

                      {pdfUrl && (
                        <div className="flex flex-col items-center gap-2">
                          <Button
                            variant="outline"
                            className="gap-2"
                            asChild
                          >
                            <a
                              href={pdfUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FileText className="h-4 w-4" />
                              <span>View PDF</span>
                            </a>
                          </Button>
                          <p className="text-xs text-muted-foreground">
                            Opens in a new tab with the {generatedMonthLabel || "selected"} PDF so you can view or download it.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}


